export type CycleStatus = 'DRAFT' | 'OPEN' | 'IN_REVIEW' | 'CALIBRATION' | 'CLOSED' | 'CANCELLED'

export type AppraisalStatus =
  | 'DRAFT' | 'SELF_REVIEW' | 'MANAGER_REVIEW' | 'CALIBRATION' | 'COMPLETED' | 'CANCELLED'

export type ReviewType = 'SELF' | 'MANAGER' | 'PEER' | 'UPWARD'

export type GoalStatus =
  | 'DRAFT' | 'ACTIVE' | 'ACHIEVED' | 'PARTIALLY_ACHIEVED' | 'MISSED' | 'CANCELLED'

export type GoalLevel = 'COMPANY' | 'DEPARTMENT' | 'TEAM' | 'INDIVIDUAL'

export type MeasureType = 'PERCENT' | 'NUMERIC' | 'MILESTONE' | 'BOOLEAN'

export type Confidence = 'ON_TRACK' | 'AT_RISK' | 'OFF_TRACK'

export type PipStatus =
  | 'DRAFT' | 'ACTIVE' | 'UNDER_REVIEW' | 'SUCCESSFUL' | 'UNSUCCESSFUL' | 'CANCELLED'

export const CYCLE_STATUS_LABELS: Record<CycleStatus, string> = {
  DRAFT: 'Draft',
  OPEN: 'Open',
  IN_REVIEW: 'In review',
  CALIBRATION: 'In calibration',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
}

export const APPRAISAL_STATUS_LABELS: Record<AppraisalStatus, string> = {
  DRAFT: 'Not started',
  SELF_REVIEW: 'Awaiting reviews',
  MANAGER_REVIEW: 'Reviews in',
  CALIBRATION: 'In calibration',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

export const REVIEW_TYPE_LABELS: Record<ReviewType, string> = {
  SELF: 'Self',
  MANAGER: 'Manager',
  PEER: 'Peer',
  UPWARD: 'Upward',
}

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  ACHIEVED: 'Achieved',
  PARTIALLY_ACHIEVED: 'Partly achieved',
  MISSED: 'Missed',
  CANCELLED: 'Cancelled',
}

export const GOAL_LEVEL_LABELS: Record<GoalLevel, string> = {
  COMPANY: 'Company',
  DEPARTMENT: 'Department',
  TEAM: 'Team',
  INDIVIDUAL: 'Individual',
}

export const MEASURE_LABELS: Record<MeasureType, string> = {
  PERCENT: 'Percentage complete',
  NUMERIC: 'A number against a target',
  MILESTONE: 'Milestones',
  BOOLEAN: 'Done or not done',
}

/** A percentage rising steadily can still be off track against the date. */
export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  ON_TRACK: 'On track',
  AT_RISK: 'At risk',
  OFF_TRACK: 'Off track',
}

export const PIP_STATUS_LABELS: Record<PipStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Running',
  UNDER_REVIEW: 'Under review',
  SUCCESSFUL: 'Met the expectations',
  UNSUCCESSFUL: 'Did not meet the expectations',
  CANCELLED: 'Cancelled',
}

export interface RatingScaleLevelView {
  id: string
  scaleId: string
  levelValue: number
  label: string
  description: string | null
  score: number
}

export interface RatingScaleView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  /** Below this level a rating becomes a training need. Null means it never does. */
  gapBelowLevel: number | null
  active: boolean
  levels: RatingScaleLevelView[]
}

export interface CompetencyView {
  id: string
  legalEntityId: string | null
  parentId: string | null
  code: string
  name: string
  description: string | null
  category: string | null
  active: boolean
}

export interface TemplateSectionView {
  id: string
  templateId: string
  competencyId: string | null
  competencyName: string | null
  sectionOrder: number
  name: string
  weightPercent: number
  sectionKind: string
  guidance: string | null
}

export interface AppraisalTemplateView {
  id: string
  legalEntityId: string | null
  scaleId: string
  scaleName: string | null
  code: string
  name: string
  description: string | null
  includeSelf: boolean
  includeManager: boolean
  includePeer: boolean
  includeUpward: boolean
  active: boolean
  sections: TemplateSectionView[]
  totalWeight: number
  /** Set when the weights do not total 100. A cycle cannot open until they do. */
  weightWarning: string | null
}

export interface AppraisalCycleView {
  id: string
  legalEntityId: string
  templateId: string
  templateName: string | null
  code: string
  name: string
  periodStart: string
  periodEnd: string
  selfReviewFrom: string | null
  selfReviewTo: string | null
  managerReviewFrom: string | null
  managerReviewTo: string | null
  calibrationFrom: string | null
  calibrationTo: string | null
  status: string
  closedAt: string | null
  notes: string | null
  appraisalCount: number
  completedCount: number
}

export interface AppraisalRatingView {
  id: string
  reviewId: string
  sectionId: string
  sectionName: string | null
  levelId: string | null
  weightPercent: number | null
  levelScore: number | null
  comments: string | null
}

export interface AppraisalReviewView {
  id: string
  appraisalId: string
  reviewerEmployeeId: string
  reviewerName: string | null
  reviewType: string
  submitted: boolean
  submittedAt: string | null
  score: number | null
  levelId: string | null
  comments: string | null
  ratings: AppraisalRatingView[]
  /** "Not started" is a different fact from "0 of 5 rated", and reads differently. */
  progress: string
}

export interface AppraisalView {
  id: string
  cycleId: string
  cycleName: string | null
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  managerEmployeeId: string | null
  managerName: string | null
  reference: string
  status: string
  /** Null until a pass is submitted. Never zero by default. */
  finalScore: number | null
  finalLevelId: string | null
  /** What calibration changed it to. The original stays in finalScore. */
  calibratedScore: number | null
  calibratedLevelId: string | null
  completedAt: string | null
  outcomesAppliedAt: string | null
  trainingNeedsCreated: number | null
  salaryRevisionId: string | null
  summary: string | null
  reviews: AppraisalReviewView[]
}

export interface AppraisalOutcomeView {
  trainingNeedsCreated: number
  salaryRevisionId: string | null
  /** Why that many needs — including when the scale has no gap threshold at all. */
  trainingNote: string | null
  compensationNote: string | null
}

export interface GoalCheckinView {
  id: string
  goalId: string
  checkedInOn: string
  currentValue: number | null
  progressPercent: number | null
  confidence: string | null
  note: string | null
  checkedInBy: string | null
}

export interface GoalView {
  id: string
  legalEntityId: string | null
  employeeId: string | null
  employeeName: string | null
  parentGoalId: string | null
  parentGoalTitle: string | null
  cycleId: string | null
  title: string
  description: string | null
  goalLevel: string
  measureType: string
  targetValue: number | null
  currentValue: number | null
  unit: string | null
  weightPercent: number | null
  startDate: string | null
  dueDate: string | null
  status: string
  /** Null before any check-in — not zero. */
  progressPercent: number | null
  outcomeNote: string | null
  checkinCount: number
  childCount: number
}

export interface CalibrationEntryView {
  id: string
  sessionId: string
  appraisalId: string
  employeeName: string | null
  scoreBefore: number | null
  levelBeforeId: string | null
  scoreAfter: number | null
  levelAfterId: string | null
  rationale: string | null
  adjustedBy: string | null
  adjustedAt: string | null
  changed: boolean
}

export interface CalibrationSessionView {
  id: string
  legalEntityId: string
  cycleId: string
  cycleName: string | null
  name: string
  heldOn: string | null
  facilitatorUserId: string | null
  status: string
  notes: string | null
  completedAt: string | null
  entries: CalibrationEntryView[]
}

export interface PipView {
  id: string
  legalEntityId: string
  employeeId: string
  employeeName: string | null
  appraisalId: string | null
  reference: string
  reason: string
  expectations: string
  supportOffered: string | null
  startDate: string
  endDate: string
  reviewFrequencyDays: number | null
  status: string
  outcomeNote: string | null
  closedAt: string | null
  raisedBy: string | null
}
